/* eslint-disable react/require-default-props */
import React, { useState, useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import DropdownCloseContext from './dropdownContext';
import Button from '../Button';
import Typography from '../Typography';
import { FlexRow, FlexColumn } from '../GridsAndLayouts/GridsAndLayouts';

const ActionMenuOptionRow = ({ option, onPick, optionId }) => {
  const closeDropdown = useContext(DropdownCloseContext);

  const handleClick = () => {
    if (option.disabled) return;
    onPick(option.value);
    closeDropdown();
  };

  return (
    <Button
      id={optionId}
      type="button"
      role="menuitem"
      variant="link"
      fullWidth
      justify="start"
      className={['ds-dropdown-action-menu__option', option.disabled ? 'ds-dropdown-action-menu__option--disabled' : '']
        .filter(Boolean)
        .join(' ')}
      onClick={handleClick}
      disabled={Boolean(option.disabled)}
    >
      <FlexRow className="ds-dropdown-action-menu__option-inner" align="center" inline fullWidth={false} gap={8}>
        {option.icon != null && (
          <FlexRow
            className="ds-dropdown-action-menu__icon"
            align="center"
            justify="center"
            inline
            fullWidth={false}
            aria-hidden
          >
            {option.icon}
          </FlexRow>
        )}
        <Typography variant="body1" component="span" className="ds-dropdown-action-menu__label">
          {option.label}
        </Typography>
      </FlexRow>
    </Button>
  );
};

ActionMenuOptionRow.propTypes = {
  option: PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    label: PropTypes.node.isRequired,
    icon: PropTypes.node,
    disabled: PropTypes.bool,
  }).isRequired,
  onPick: PropTypes.func.isRequired,
  optionId: PropTypes.string,
};

/**
 * Figma tile-edit action menu — icon + label rows with dividers (210px panel).
 * Fires `onChange` per pick; does not persist selection in the trigger.
 */
const ActionMenu = ({
  DropdownComponent,
  options,
  onChange,
  trigger,
  triggerIcon,
  triggerAriaLabel,
  align = 'left',
  className = '',
  disabled = false,
  idPrefix,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);

  const handlePick = useCallback(
    (value) => {
      if (onChange) {
        onChange(value);
      }
    },
    [onChange]
  );

  const defaultTrigger = triggerIcon ? (
    <FlexRow
      id={idPrefix ? `${idPrefix}-trigger` : undefined}
      className={[
        'ds-dropdown-action-menu__trigger',
        'ds-dropdown-action-menu__trigger--icon',
        menuOpen && 'ds-dropdown-action-menu__trigger--open',
        disabled && 'ds-dropdown-action-menu__trigger--disabled',
      ]
        .filter(Boolean)
        .join(' ')}
      align="center"
      justify="center"
      inline
      fullWidth={false}
    >
      {triggerIcon}
    </FlexRow>
  ) : null;

  return (
    <DropdownComponent
      className={['ds-dropdown--action-menu', className].filter(Boolean).join(' ')}
      trigger={trigger ?? defaultTrigger}
      align={align}
      disabled={disabled}
      triggerAriaLabel={triggerAriaLabel}
      onOpen={() => setMenuOpen(true)}
      onClose={() => setMenuOpen(false)}
    >
      <FlexColumn
        id={idPrefix ? `${idPrefix}-menu` : undefined}
        className="ds-dropdown-action-menu__list"
        role="presentation"
        align="start"
        fullWidth
      >
        {options.map((option, index) => (
          <ActionMenuOptionRow
            key={String(option.value)}
            option={option}
            onPick={handlePick}
            optionId={idPrefix ? `${idPrefix}-option-${option.value ?? index}` : undefined}
          />
        ))}
      </FlexColumn>
    </DropdownComponent>
  );
};

ActionMenu.propTypes = {
  DropdownComponent: PropTypes.elementType.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.node.isRequired,
      icon: PropTypes.node,
      disabled: PropTypes.bool,
    })
  ).isRequired,
  onChange: PropTypes.func,
  trigger: PropTypes.node,
  triggerIcon: PropTypes.node,
  triggerAriaLabel: PropTypes.string,
  align: PropTypes.oneOf(['left', 'right', 'center']),
  className: PropTypes.string,
  disabled: PropTypes.bool,
  idPrefix: PropTypes.string,
};

export default ActionMenu;
